import { Component, inject, OnInit } from '@angular/core';
import { CommonModule, PlatformLocation } from '@angular/common';
import { RouterModule } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormlyFieldConfig, FormlyFormOptions, FormlyModule } from '@ngx-formly/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { ToastrService } from 'ngx-toastr';
import { ActionItemComponent } from '../../shared/action-item.component';
import { GroupsService } from '../groups.service';
import { UpdatePostDto } from '@oracychampions/api-interfaces';
import { v4 as uuid } from 'uuid';
import { switchMap } from 'rxjs';
import { AudioRecorderComponent } from '../../shared/formly/audio-recorder.component';
import { Game, gameInfo } from '../../core/game';

@Component({
  selector: 'oc-edit-post',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ActionItemComponent,
    ReactiveFormsModule,
    FormlyBootstrapModule,
    FormlyModule,
    AudioRecorderComponent,
  ],
  template: `
    <div class="modal-container">
      <div class="modal-spacer"></div>
      <div class="modal-body">
        <h1 *ngIf="!id">Add Post</h1>
        <h1 *ngIf="id">Edit Post</h1>
        <form [formGroup]="form" (ngSubmit)="onSubmit(model)">
          <formly-form [form]="form" [fields]="fields" [model]="model" [options]="options"></formly-form>
          <div class="d-flex gap-2">
            <button type="submit" class="btn btn-primary mt-3 mb-4">
              <span *ngIf="saving" class="spinner-border spinner-border-sm"></span>
              <span *ngIf="!saving">Save Changes</span>
            </button>
            <button type="button" class="btn btn-secondary mt-3 mb-4" (click)="activeModal.dismiss('Cancel')">
              Cancel
            </button>
          </div>
        </form>
      </div>
      <div class="modal-spacer">
        <oc-action-item icon="tablerX" size="lg" (click)="activeModal.dismiss('Cross click')"></oc-action-item>
      </div>
    </div>
  `,
  styles: `
    .modal-container {
      display: flex;
      min-height: 100vh;
    }

    .modal-body {
      flex: 0;
      flex-basis: 560px;
      padding: 2.5rem 3.5rem;
    }

    .modal-spacer {
      flex: 1; 
    }

    .modal-spacer:last-child {
      padding: 2rem;
      display: flex;
      justify-content: flex-end;
      align-items: flex-start;
    }

    h1 {
      margin-bottom: 2rem;
    }
  `,
})
export class EditPostComponent implements OnInit {
  private platformLocation = inject(PlatformLocation);
  private modalService = inject(NgbModal);
  private toastr = inject(ToastrService);
  private groupsService = inject(GroupsService);
  public activeModal = inject(NgbActiveModal);

  public id: string | null = null;
  public groupId: string | null = null;

  private games = gameInfo;

  saving = false;
  options: FormlyFormOptions = {
    formState: {
      submitted: false,
    },
  };

  form = new FormGroup({});
  model = { title: '', audio: null, students: [], game: Game.None };
  fields: FormlyFieldConfig[];

  constructor() {
    this.platformLocation.onPopState(() => this.modalService.dismissAll());
    this.games.pop();
  }

  ngOnInit() {
    this.fields = [
      {
        key: 'title',
        type: 'input',
        props: {
          label: 'Title',
          required: true,
        },
      },
      {
        key: 'students',
        type: 'multiselect',
        props: {
          label: 'Students',
          required: true,
          options: this.groupsService.getStudents(this.groupId!),
        },
      },
      {
        key: 'audio',
        type: 'record-audio',
        props: {
          label: 'Audio',
          required: true,
        },
      },
      {
        key: 'game',
        type: 'select',
        props: {
          label: 'Game',
          required: true,
          options: this.games,
        },
      },
    ];
  }

  onSubmit(model: any) {
    this.options.formState.submitted = true;
    if (this.saving) {
      return;
    }
    if (!this.form.valid) {
      this.toastr.error('Please review and try again');
      return;
    }

    this.saving = true;

    const audioId = uuid();

    console.log(model);

    const post: UpdatePostDto = {
      ...model,
      audioId,
      levels: JSON.stringify(model.audio.levels),
      groupId: this.groupId,
    };

    if (this.id) {
      this.groupsService.updatePost(this.id, post).subscribe((response) => {
        this.toastr.success('Post updated');
        this.activeModal.close(response);
      });
    } else {
      this.groupsService
        .uploadAudio(audioId, model.audio.blob)
        .pipe(
          switchMap(() => {
            return this.groupsService.createPost(post);
          }),
        )
        .subscribe((response) => {
          this.toastr.success('Post created');
          this.activeModal.close(response);
        });
    }
  }
}
